/*
 * Rapunzel theme — único archivo JS (cargado con defer, sin dependencias).
 * Módulos: diálogos (menú, carrito, zoom) · header al hacer scroll · carrito AJAX ·
 * selector de packs · galería · barra fija móvil · aparición al scroll.
 * Analítica: no se agregan scripts de seguimiento; los eventos estándar de Shopify
 * (view_item, add_to_cart, begin_checkout) se conservan usando el formulario y las rutas nativas.
 */
(() => {
  'use strict';

  const strings = window.rpStrings || {};
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const designMode = Boolean(window.Shopify && window.Shopify.designMode);

  /* ---------------------------------------------------------------- Diálogos */

  const openDialog = (dialog, opener) => {
    if (!dialog || dialog.open) return;
    dialog.showModal();
    if (opener) {
      opener.setAttribute('aria-expanded', 'true');
      dialog._rpOpener = opener;
    }
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-rp-open-dialog]');
    if (opener) {
      event.preventDefault();
      openDialog(document.getElementById(opener.dataset.rpOpenDialog), opener);
      return;
    }

    const closer = event.target.closest('[data-rp-close-dialog]');
    if (closer) {
      const dialog = closer.closest('dialog');
      if (dialog) closeDialog(dialog);
      return;
    }

    // Clic en el fondo (backdrop) cierra el diálogo.
    if (event.target.matches('dialog[data-rp-dialog][open]')) {
      const rect = event.target.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left && event.clientX <= rect.right &&
        event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener(
    'close',
    (event) => {
      const dialog = event.target;
      if (!(dialog instanceof HTMLDialogElement) || !dialog._rpOpener) return;
      dialog._rpOpener.setAttribute('aria-expanded', 'false');
      dialog._rpOpener = null;
    },
    true
  );

  /* ------------------------------------------------------------------ Header */

  const headerWrapper = document.querySelector('.rp-header-wrapper');
  if (headerWrapper) {
    let ticking = false;
    const update = () => {
      headerWrapper.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          window.requestAnimationFrame(update);
          ticking = true;
        }
      },
      { passive: true }
    );
    update();
  }

  /* ----------------------------------------------------------------- Carrito */

  const getDrawer = () => document.querySelector('[data-rp-cart-drawer]');

  const renderCart = (sectionHtml) => {
    const drawer = getDrawer();
    if (!drawer || !sectionHtml) return;
    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const next = doc.querySelector('[data-rp-cart-contents]');
    const current = drawer.querySelector('[data-rp-cart-contents]');
    if (!next || !current) return;

    const hadFocus = current.contains(document.activeElement);
    current.innerHTML = next.innerHTML;

    const count = Number(next.dataset.cartCount || 0);
    current.dataset.cartCount = String(count);
    drawer.toggleAttribute('data-empty', count === 0);
    document.querySelectorAll('[data-rp-cart-count]').forEach((bubble) => {
      bubble.textContent = String(count);
      bubble.hidden = count === 0;
    });

    if (hadFocus) {
      const target = drawer.querySelector('[data-rp-cart-contents] button, [data-rp-cart-contents] a') ||
        drawer.querySelector('[data-rp-close-dialog]');
      if (target) target.focus();
    }
  };

  const sectionsPayload = () => ({ sections: 'cart-drawer', sections_url: window.location.pathname });

  const setBusy = (buttons, busy) => {
    buttons.forEach((button) => {
      button.classList.toggle('is-loading', busy);
      button.setAttribute('aria-busy', String(busy));
      if (busy) button.disabled = true;
      else if (button.dataset.soldOut !== 'true') button.disabled = false;
    });
  };

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[data-rp-product-form]');
    if (!form || !window.fetch) return;
    event.preventDefault();

    const buttons = [
      ...form.querySelectorAll('[data-rp-add-button]'),
      ...(form.id ? document.querySelectorAll(`[data-rp-add-button][form="${form.id}"]`) : []),
    ];
    const errorBox = form.querySelector('[data-rp-form-error]');
    if (errorBox) errorBox.hidden = true;
    setBusy(buttons, true);

    const body = new FormData(form);
    const payload = sectionsPayload();
    body.append('sections', payload.sections);
    body.append('sections_url', payload.sections_url);

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || strings.addError);
      }
      renderCart(data.sections && data.sections['cart-drawer']);
      openDialog(getDrawer());
    } catch (error) {
      if (errorBox) {
        errorBox.textContent = (error && error.message) || strings.addError || '';
        errorBox.hidden = false;
      }
    } finally {
      setBusy(buttons, false);
    }
  });

  document.addEventListener('click', async (event) => {
    const toggle = event.target.closest('[data-rp-cart-toggle]');
    if (toggle && getDrawer()) {
      event.preventDefault();
      openDialog(getDrawer(), toggle);
      return;
    }

    const change = event.target.closest('[data-rp-cart-change]');
    if (!change) return;
    event.preventDefault();
    const drawer = getDrawer();
    drawer && drawer.setAttribute('aria-busy', 'true');

    try {
      const response = await fetch(`${root}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          id: change.dataset.key,
          quantity: Number(change.dataset.quantity),
          ...sectionsPayload(),
        }),
      });
      const data = await response.json();
      if (!response.ok || data.status) throw new Error(data.description || data.message);
      renderCart(data.sections && data.sections['cart-drawer']);
    } catch (error) {
      const box = drawer && drawer.querySelector('[data-rp-cart-error]');
      if (box) {
        box.textContent = (error && error.message) || strings.cartError || '';
        box.hidden = false;
      }
    } finally {
      drawer && drawer.removeAttribute('aria-busy');
    }
  });

  /* ------------------------------------------------- Producto: packs y galería */

  const initGallery = (gallery) => {
    const track = gallery.querySelector('[data-rp-gallery-track]');
    if (!track) return null;
    const slides = [...track.querySelectorAll('.rp-gallery__slide')];
    const thumbs = [...gallery.querySelectorAll('[data-rp-gallery-thumb]')];
    const dots = [...gallery.querySelectorAll('.rp-gallery__dot')];
    const status = gallery.querySelector('[data-rp-gallery-status]');

    const setActive = (index) => {
      thumbs.forEach((thumb, i) => thumb.toggleAttribute('aria-current', i === index));
      dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));
      if (status && strings.galleryStatus) {
        status.textContent = strings.galleryStatus
          .replace('[current]', index + 1)
          .replace('[total]', slides.length);
      }
    };

    const goTo = (index) => {
      const slide = slides[index];
      if (!slide) return;
      track.scrollTo({ left: slide.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      setActive(index);
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => goTo(Number(thumb.dataset.rpGalleryThumb))));

    if ('IntersectionObserver' in window && slides.length > 1) {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) setActive(Number(entry.target.dataset.index));
          });
        },
        { root: track, threshold: 0.6 }
      );
      slides.forEach((slide) => observer.observe(slide));
    }

    return {
      goToMedia(mediaId) {
        if (!mediaId) return;
        const index = slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
        if (index > -1) goTo(index);
      },
    };
  };

  const initZoom = (scope) => {
    const dialog = scope.querySelector('[data-rp-zoom]');
    if (!dialog) return;
    const stage = dialog.querySelector('[data-rp-zoom-stage]');
    const image = document.createElement('img');
    image.className = 'rp-zoom__image';
    stage.appendChild(image);
    scope.querySelectorAll('[data-rp-zoom-src]').forEach((button) => {
      button.addEventListener('click', () => {
        image.src = button.dataset.rpZoomSrc;
        image.alt = button.dataset.rpZoomAlt || '';
        openDialog(dialog, button);
      });
    });
  };

  const initStickyBar = (scope) => {
    const bar = scope.querySelector('[data-rp-sticky-buy]');
    const mainBuy = scope.querySelector('[data-rp-main-buy]');
    if (!bar || !mainBuy || !('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', show);
      bar.inert = !show;
      document.body.classList.toggle('rp-has-sticky-buy', show);
    });
    observer.observe(mainBuy);
  };

  const initProduct = (scope) => {
    if (scope._rpReady) return;
    scope._rpReady = true;

    const galleryEl = scope.querySelector('[data-rp-gallery]');
    const gallery = galleryEl ? initGallery(galleryEl) : null;
    initZoom(scope);
    initStickyBar(scope);

    scope.querySelectorAll('[data-rp-packs]').forEach((fieldset) => {
      const form = document.getElementById(fieldset.dataset.formId);
      fieldset.addEventListener('change', (event) => {
        const radio = event.target.closest('input[type="radio"]');
        if (!radio || !form) return;
        const available = radio.dataset.available === 'true';

        const idInput = form.querySelector('[data-rp-variant-id]');
        if (idInput) {
          idInput.value = radio.value;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        scope.querySelectorAll('[data-rp-price]').forEach((el) => { el.textContent = radio.dataset.price; });
        scope.querySelectorAll('[data-rp-compare]').forEach((el) => {
          el.textContent = radio.dataset.compare || '';
          el.hidden = !radio.dataset.compare;
        });
        scope.querySelectorAll('[data-rp-variant-title]').forEach((el) => { el.textContent = radio.dataset.title; });

        const stickyImage = scope.querySelector('[data-rp-sticky-image]');
        if (stickyImage && radio.dataset.image) {
          stickyImage.removeAttribute('srcset');
          stickyImage.src = radio.dataset.image;
        }

        const buttons = [
          ...form.querySelectorAll('[data-rp-add-button]'),
          ...document.querySelectorAll(`[data-rp-add-button][form="${form.id}"]`),
        ];
        buttons.forEach((button) => {
          const label = button.querySelector('[data-rp-add-label]');
          button.dataset.soldOut = String(!available);
          button.disabled = !available;
          if (!label) return;
          if (!available) label.textContent = button.dataset.labelSoldOut;
          else if ('noPrice' in button.dataset) label.textContent = button.dataset.labelAdd;
          else label.textContent = `${button.dataset.labelAdd} · ${radio.dataset.price}`;
        });

        if (gallery) gallery.goToMedia(radio.dataset.mediaId);

        if (scope.dataset.updateUrl === 'true') {
          const url = new URL(window.location.href);
          url.searchParams.set('variant', radio.value);
          window.history.replaceState({}, '', url.toString());
        }
      });
    });
  };

  /* ------------------------------------------------------- Aparición al scroll */

  const initReveal = (scope) => {
    const items = [...scope.querySelectorAll('[data-rp-reveal]:not(.is-revealed)')];
    if (!items.length) return;
    const revealAll = () => items.forEach((item) => item.classList.add('is-revealed'));
    if (designMode || reducedMotion.matches || !('IntersectionObserver' in window) ||
        !document.body.classList.contains('rp-reveal-enabled')) {
      revealAll();
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.05 }
    );
    items.forEach((item) => observer.observe(item));
  };

  /* -------------------------------------------------------------- Arranque */

  const init = (scope = document) => {
    scope.querySelectorAll('[data-rp-product]').forEach(initProduct);
    initReveal(scope);
  };

  init();

  // Editor de temas: re-inicializar secciones y abrir el acordeón del bloque seleccionado.
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:block:select', (event) => {
    const details = event.target.closest('details') || event.target.querySelector('details');
    if (details) details.open = true;
  });
})();
